import { markingApi } from '../api/marking';

/**
 * Известные AI (Application Identifiers) Честного Знака и их длины.
 * Фиксированная длина = данные ровно N символов, разделитель не нужен.
 * null = переменная длина, данные до GS-разделителя или следующего AI.
 */
const KNOWN_AIS: [string, number | null][] = [
  ['01', 14],   // GTIN (фиксированная)
  ['21', null], // Серийный номер (переменная)
  ['91', null], // Код проверки (переменная, обычно 4 символа)
  ['92', null], // Криптоподпись (переменная, обычно 44 символа)
  ['93', null], // Криптохвост (старый формат, переменная)
];

/**
 * Проверяет, что raw_code содержит полный код маркировки, включая криптохвост
 * (91 + 92 или 93). Обрезанный marking_code (только 01 + GTIN + 21 + serial)
 * не должен передаваться в генератор DataMatrix — ЧЗ отвергнет его как неполный.
 */
export function hasCryptoTail(raw: string): boolean {
  const GS = '\x1d';
  let text = raw;
  if (text.startsWith(GS)) text = text.substring(1);
  if (text.startsWith(']d2')) text = text.substring(3);

  if (!text.startsWith('01') || text.length < 16) return false;

  // 01 + 14 цифр GTIN
  let pos = 16;
  if (text[pos] === GS) pos++;

  // серийный номер: AI 21
  if (!text.startsWith('21', pos)) return false;
  pos += 2;

  const gsIdx = text.indexOf(GS, pos);
  if (gsIdx < 0) return false;

  // после GS должен идти AI 91/92/93
  const nextAi = text.substring(gsIdx + 1, gsIdx + 3);
  return /^9[123]$/.test(nextAi);
}

/**
 * Преобразует сырой Data Matrix код (с \x1d разделителями) в GS1 формат
 * с AI в скобках: (01)GTIN(21)serial(91)verif(92)crypto.
 * Возвращает null, если код не распознан как GS1 или не содержит криптохвост.
 */
export function rawToGs1(raw: string): string | null {
  if (!hasCryptoTail(raw)) return null;

  const GS = '\x1d';
  let text = raw;
  if (text.startsWith(GS)) text = text.substring(1);
  if (text.startsWith(']d2')) text = text.substring(3);

  if (!text.startsWith('01') || text.length < 16) return null;

  let gs1 = '';
  let pos = 0;

  while (pos < text.length) {
    if (text[pos] === GS) { pos++; continue; }

    const ai = KNOWN_AIS.find(([prefix]) => text.startsWith(prefix, pos));
    if (!ai) break;

    const [prefix, fixedLen] = ai;
    pos += prefix.length;

    let value: string;
    if (fixedLen !== null) {
      value = text.substring(pos, pos + fixedLen);
      pos += fixedLen;
    } else {
      let end = text.indexOf(GS, pos);
      if (end < 0) end = text.length;
      value = text.substring(pos, end);
      pos = end;
    }

    gs1 += `(${prefix})${value}`;
  }

  return gs1.startsWith('(01)') ? gs1 : null;
}

/**
 * Человекочитаемая часть (HRI) кода Честного Знака в конкатенированном формате,
 * как её печатают на реальных марках: «01<GTIN>21<serial>» — без скобок вокруг AI.
 * Пример: 010465516695619521SLP8%5QoO/nA?
 *
 * Криптохвост (91/92/93) в HRI не выводится.
 * Возвращает null, если код не распознан.
 */
export function extractHri(raw: string): string | null {
  const gs1 = rawToGs1(raw);
  if (!gs1) return null;
  const m = gs1.match(/^\(01\)(\d{14})(?:\(21\)([^(]*))?/);
  if (!m) return null;
  let result = `01${m[1]}`;
  if (m[2]) result += `21${m[2]}`;
  return result;
}

/**
 * Эскейпит символы, которые ломают парсер `gs1datamatrix`:
 * - `(` → `^040` (ASCII 40), иначе bwip-js считает началом нового AI
 * - `)` → `^041` (ASCII 41), чтобы не съехал парсинг
 * - `^`  → `^094` (ASCII 94), т.к. сам символ `^` — префикс escape
 *
 * Требует `parse: true` в опциях bwip-js — тогда эти escape декодируются
 * обратно в исходные символы при кодировании матрицы.
 */
function escapeGs1Value(value: string): string {
  return value
    .replace(/\^/g, '^094')
    .replace(/\(/g, '^040')
    .replace(/\)/g, '^041');
}

/**
 * Готовит строку для bwip-js `gs1datamatrix` + `parse: true`:
 * парентез-нотация (01)GTIN(21)serial(91)verif(92)crypto.
 *
 * bwip-js сам расставляет FNC1 в начале и GS-разделители между
 * переменными AI. Это даёт байты, совпадающие с оригинальным
 * отсканированным кодом ЧЗ (проверено декодированием zxing-cpp).
 */
export function prepareGs1DataMatrixText(raw: string): string | null {
  return rawToGs1(raw);
}

/**
 * Загружает SVG DataMatrix с бэкенда (zint с GS-разделителями) и
 * возвращает готовый HTMLImageElement. Используется для генерации PDF.
 */
export async function renderDataMatrixImage(code: string, scale = 6): Promise<HTMLImageElement> {
  if (!hasCryptoTail(code)) {
    throw new Error('DataMatrix: код не содержит криптохвост (91/92/93). Возможно, передан marking_code вместо raw_code.');
  }

  const url = await markingApi.generateDataMatrixSvg(code, scale);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Не удалось загрузить DataMatrix'));
    };
    img.src = url;
  });
}
